import React from 'react';

import { Component } from '@paragon/contract-library';
import { hasTrussDesignPermission } from '@paragon/login-tools';

import { useAppSelector } from '../components/hooks';
import { getSingleSelectedItemEntryOrNull } from '../store/selectors/getSelectedItemSelector';
import { isInsertingComponentDesign as isInsertingComponentDesignSelector } from '../store/selectors/isInsertingComponentDesignSelector';
import { inViewOnlyMode } from '../store/selectors/viewOnlyModeSelectors';
import { AnalogJointEditorHandle } from './editor/AnalogJointEditorHandle';
import { BearingEditorHandle } from './editor/BearingEditorHandle';
import { OffsetReferenceLineHandle } from './editor/OffsetReferenceLineHandle';
import { PlatePairEditorHandle } from './editor/PlatePairEditorHandle';
import { SpliceEditorHandle } from './editor/SpliceEditorHandle';
import { WebJointEditorHandle } from './editor/WebJointEditorHandle';

interface Props {
  readonly component: Component;
  readonly componentDesignGuid: string;
  readonly setEditedComponent: (component: Component) => void;
}

export function ComponentDesignEditorHandle({ component, componentDesignGuid, setEditedComponent }: Props) {
  const selectedItem = useAppSelector(getSingleSelectedItemEntryOrNull);
  const isViewOnly = useAppSelector(inViewOnlyMode);
  const isInsertingComponentDesign = useAppSelector(isInsertingComponentDesignSelector);
  const canDesignTrusses = useAppSelector(hasTrussDesignPermission);

  // The component is considered "view only" during insertion, and we need to allow editor handles during profile
  // insertion for web joints.
  if (!selectedItem || (!isInsertingComponentDesign && isViewOnly) || !canDesignTrusses) return null;

  switch (selectedItem.type) {
    case 'analogJoint':
      return <AnalogJointEditorHandle analogJoint={selectedItem.item} />;

    case 'bearing':
      return <BearingEditorHandle bearing={selectedItem.item} />;

    case 'platePair':
      const platePair = component.platePairs.find((platePair) => platePair.guid === selectedItem.item.guid);
      if (!platePair) return null;
      return (
        <PlatePairEditorHandle
          platePair={platePair}
          component={component}
          componentDesignGuid={componentDesignGuid}
          setEditedComponent={setEditedComponent}
        />
      );

    case 'referenceLine':
      return <OffsetReferenceLineHandle line={selectedItem.item} />;

    case 'webJoint':
      return <WebJointEditorHandle webJoint={selectedItem.item} />;

    case 'splice':
      return <SpliceEditorHandle splice={selectedItem.item} />;

    default:
      return null;
  }
}
